import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import MembershipCard from "@/components/MembershipCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { HeartPulse, Gift, Users, Award, ArrowRight, Sparkles } from "lucide-react";

export default function ClinicLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/tenants/by-slug/${slug}`);
        setTenant(data);
      } catch (e) {
        setError("Clinic not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // If user is already a patient of THIS tenant, jump to dashboard
  useEffect(() => {
    if (user?.role === "patient" && tenant && user.tenant_id === tenant.id) {
      navigate(`/c/${slug}/dashboard`);
    }
  }, [user, tenant, slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-mr-bg p-10">
        <Skeleton className="h-12 w-80 mb-6" /><Skeleton className="h-6 w-2/3 mb-3" /><Skeleton className="h-6 w-1/2" />
      </div>
    );
  }
  if (error || !tenant) {
    return (
      <div className="min-h-screen bg-mr-bg flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-display text-3xl font-semibold text-mr-primary">Clinic not found</h1>
          <p className="text-mr-secondary mt-2">We couldn't find a clinic at <strong>/{slug}</strong>.</p>
          <Link to="/"><Button className="mt-6 rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">Back to home</Button></Link>
        </div>
      </div>
    );
  }

  const primary = tenant.brand_color || "#0F8964";
  const accent = tenant.accent_color || "#0284C7";
  const logo = tenant.logo_path ? fileUrl(tenant.logo_path) : null;

  return (
    <div className="min-h-screen bg-mr-bg" style={{ "--mr-primary": primary, "--mr-accent": accent }}>
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to={`/c/${slug}`} className="flex items-center gap-3" data-testid="clinic-header-logo">
            {logo ? <img src={logo} alt={tenant.name} className="h-8 w-8 rounded-lg object-cover" /> :
              <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white" style={{ background: primary }}>
                <Sparkles className="h-4 w-4" />
              </div>}
            <div className="leading-tight">
              <p className="font-display font-semibold text-[15px]" style={{ color: primary }}>{tenant.name}</p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-mr-secondary">Loyalty Programme</p>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to={`/c/${slug}/login`}><Button data-testid="clinic-signin-btn" variant="ghost" size="sm" className="rounded-full">Sign in</Button></Link>
            <Link to={`/c/${slug}/register`}>
              <Button data-testid="clinic-join-btn" size="sm" className="rounded-full text-white hover:opacity-90" style={{ background: primary }}>
                Join now
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <span className="label-eyebrow" style={{ color: accent }}>{tenant.name} · Membership</span>
          <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4" style={{ color: primary }}>
            Earn rewards every time you choose health.
          </h1>
          <p className="text-mr-secondary text-lg mt-6 max-w-xl leading-relaxed">
            Get points for screenings, vaccinations, dental visits, physiotherapy and more.
            Redeem for consultations, lab packages and exclusive partner offers.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link to={`/c/${slug}/register`}>
              <Button data-testid="clinic-cta-join" size="lg" className="rounded-full text-white hover:opacity-90 px-7" style={{ background: primary }}>
                Become a member <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to={`/c/${slug}/login`}>
              <Button data-testid="clinic-cta-signin" size="lg" variant="ghost" className="rounded-full">I already have an account</Button>
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5 flex justify-center animate-fade-up" style={{ animationDelay: "120ms" }}>
          <MembershipCard
            tenant={tenant}
            user={{ name: "Your name here", tier: "Standard", id: "preview-card-id" }}
            variant={tenant.card_template}
          />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: HeartPulse, title: "200+ pts", body: "for annual health screenings" },
            { icon: Gift, title: "Welcome bonus", body: "join today and get instant points" },
            { icon: Award, title: "5 tier system", body: "from Standard to VIP" },
            { icon: Users, title: "Family accounts", body: "earn together with loved ones" },
          ].map((f, i) => {
            const I = f.icon;
            return (
              <div key={i} className="bg-white border border-mr rounded-2xl p-6 animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <I className="h-5 w-5" style={{ color: accent }} />
                <p className="font-display text-lg font-semibold mt-3" style={{ color: primary }}>{f.title}</p>
                <p className="text-mr-secondary text-sm mt-1">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
